import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * AppUpdateWatcher — detecta que hay una versión nueva publicada y recarga.
 *
 * Problema que resuelve: Safari deja la app abierta días con el bundle viejo
 * (la navegación interna no vuelve a pedir index.html), así que el usuario ve
 * pantallas que ya no existen. Al volver a la pestaña o cada 10 minutos se
 * pide index.html sin caché y se compara el nombre del bundle principal; si
 * cambió, se recarga en el siguiente cambio de pantalla (nunca a media sesión
 * de entrenamiento) y se avisa con un toast.
 */
const bundleOf = (html) => (html.match(/static\/js\/main\.[a-z0-9]+\.js/) || [])[0] || null;
const currentBundle = () => {
  const s = Array.from(document.scripts).map((x) => x.src).find((src) => /static\/js\/main\.[a-z0-9]+\.js/.test(src));
  return s ? bundleOf(s) : null;
};
const inWorkout = (pathname) => pathname.startsWith('/app/workout/run');

const AppUpdateWatcher = () => {
  const location = useLocation();
  const pending = useRef(false);
  const toasted = useRef(false);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return undefined;
    const mine = currentBundle();
    if (!mine) return undefined;
    let alive = true;
    const check = async () => {
      try {
        const res = await fetch(`/index.html?v=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const latest = bundleOf(await res.text());
        if (alive && latest && latest !== mine) {
          pending.current = true;
          if (!inWorkout(window.location.pathname)) {
            window.location.reload();
          } else if (!toasted.current) {
            toasted.current = true;
            toast.info('Hay una versión nueva de ONOR. Se actualizará al terminar tu sesión.');
          }
        }
      } catch (e) { /* sin red */ }
    };
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    const t = setInterval(check, 10 * 60 * 1000);
    const first = setTimeout(check, 4000);
    return () => { alive = false; clearInterval(t); clearTimeout(first); document.removeEventListener('visibilitychange', onVisible); };
  }, []);

  useEffect(() => {
    if (pending.current && !inWorkout(location.pathname)) window.location.reload();
  }, [location.pathname]);

  return null;
};

export default AppUpdateWatcher;
